<script lang="ts">
	import { base } from '$app/paths';
	import * as api from '$lib/shared/apis';

	let email = '';
	let name = '';
	let password = '';
	let passwordRepeat = '';
	let error = '';
	let done = false;
	let resent = false;

	let acceptPrivacy = false;
	let acceptTerms = false;

	const register = async () => {
		error = '';
		if (password !== passwordRepeat) {
			error = 'Passwörter stimmen nicht überein';
			return;
		}
		const { response, json } = await api.post({
			query: /* GraphQL */ `
				mutation register(
					$email: String!
					$name: String!
					$password: String!
					$acceptTerms: Boolean!
					$receiveNewsletter: [ProjectOwner!]!
					$projectName: ProjectName!
				) {
					register(
						email: $email
						name: $name
						password: $password
						termsAccepted: $acceptTerms
						receiveNewsletter: $receiveNewsletter
						project: $projectName
					) {
						ok
						error
					}
				}
			`,
			variables: {
				email,
				name,
				password,
				acceptTerms: acceptPrivacy && acceptTerms,
				receiveNewsletter,
				projectName: import.meta.env.VITE_PROJECT_NAME,
			},
		});

		if (response.status === 200) {
			if (json.data.register.ok) {
				done = true;
			} else {
				error = json.data.register.error;
			}
		}
	};

	const resendVerification = async () => {
		const { response, json } = await api.post({
			query: /* GraphQL */ `
				mutation resendVerificationEmail($email: String!, $projectName: ProjectName!) {
					resendVerificationEmail(email: $email, project: $projectName) {
						ok
						error
					}
				}
			`,
			variables: {
				email,
				projectName: import.meta.env.VITE_PROJECT_NAME,
			},
		});

		if (response.status === 200) {
			if (json.data.resendVerificationEmail.ok) {
				resent = true;
			} else {
				error = json.data.resendVerificationEmail.error;
			}
		}
	};

	let receiveNewsletter: ProjectOwner[] = [];
</script>

{#if !done}
	<form on:submit|preventDefault={register}>
		<label for="email">E-Mail-Adresse</label>
		<input type="email" id="email" bind:value={email} required autocomplete="email" />
		<label for="name">Benutzer*innenname</label>
		<input type="text" id="name" bind:value={name} required autocomplete="name" />
		<label for="password">Passwort</label>
		<input
			type="password"
			id="password"
			bind:value={password}
			required
			autocomplete="new-password"
		/>
		<label for="passwordRepeat">Passwort bestätigen</label>
		<input type="password" id="passwordRepeat" bind:value={passwordRepeat} required />
		<div class="flex items-start mt-5 relative">
			<input type="checkbox" id="acceptPrivacy" bind:checked={acceptPrivacy} required /><span
				class="checkbox-indicator"
			/>
			<div class="ml-3">
				<label for="acceptPrivacy"
					>Ich habe die Datenschutzerklärung gelesen und erkläre mich damit einverstanden.</label
				><a
					class="text-sm-fluid underline"
					href="{base}/datenschutz"
					target="_blank"
					rel="noreferrer">Datenschutzerklärung lesen</a
				>
			</div>
		</div>
		<div class="flex items-start mt-5 relative">
			<input type="checkbox" id="acceptTerms" bind:checked={acceptTerms} required /><span
				class="checkbox-indicator"
			/>
			<div class="ml-3">
				<label for="acceptTerms"
					>Ich habe die Nutzungsbedingungen gelesen und erkläre mich damit einverstanden.</label
				><a
					class="text-sm-fluid underline"
					href="{base}/nutzungsbedingungen"
					target="_blank"
					rel="noreferrer">Nutzungsbedingungen lesen</a
				>
			</div>
		</div>

		<p class="mt-5 text-sm-fluid uppercase">
			Ich möchte Benachrichtigungen zu Crowdsourcingprojekten
		</p>
		<div class="flex items-start mt-5 relative">
			<input
				type="checkbox"
				id="newsletter-wbr"
				bind:group={receiveNewsletter}
				value="WBR"
			/><span class="checkbox-indicator" />
			<div class="ml-3">
				<label for="newsletter-wbr">aus der Wienbibliothek im Rathaus</label>
			</div>
		</div>
		<div class="flex items-start mt-5 relative">
			<input
				type="checkbox"
				id="newsletter-wm"
				bind:group={receiveNewsletter}
				value="WM"
			/><span class="checkbox-indicator" />
			<div class="ml-3">
				<label for="newsletter-wm">aus dem Wien Museum</label>
			</div>
		</div>

		{#if error}
			<p class="text-sm-fluid text-orange mt-3" aria-live="assertive">{error}</p>

			{#if error === 'E-Mail wird bereits verwendet.'}
				<button
					class="text-sm-fluid underline text-left mt-2"
					type="button"
					on:click={resendVerification}>Verifizierungs-E-Mail erneut schicken</button
				>
			{/if}
		{/if}
		<button type="submit" class="button mx-auto mt-10">Registrieren</button>
	</form>
	{#if resent}
		<p aria-live="polite">Bitte überprüfen Sie Ihren E-Mail-Posteingang.</p>
	{/if}
{:else}
	<div aria-live="polite" class="mx-auto max-w-2xl">
		<p class="text-lg-fluid">Danke fürs Mitmachen! Wir freuen uns auf Ihre Beiträge.</p>
		<p class="mt-8">
			Für den Abschluss Ihrer Anmeldung wurde ein Aktivierungslink an {email} verschickt.
		</p>
		<p class="mt-5">
			Wenn Sie kein E-Mail bekommen haben, kontaktieren Sie uns bitte unter <a
				href="mailto:crowdsourcing@wienbibliothek.at">crowdsourcing@wienbibliothek.at</a
			>.
		</p>
	</div>
{/if}

<style lang="postcss">
	form {
		display: flex;
		flex-direction: column;
		margin-inline: auto;
		max-inline-size: 30rem;
	}
	label {
		@apply text-sm-fluid uppercase;
	}

	input {
		&[type='text'],
		&[type='email'],
		&[type='password'] {
			background-color: transparent;
			margin-block-start: 0.25rem;
			padding-block: 0.625rem;
			text-align: center;

			& + label {
				margin-block-start: 1.25rem;
			}
		}
	}
</style>
